import { DateTime } from 'luxon';
import { isExternalSyncReadOnly } from 'shared/utils/knowledge.utils';
import { EXPIRY_STATUS } from './constants';

/**
 * A row is read-only when the item itself is synced, or when it lives in a synced binder —
 * my-content rows carry their binders in `links`, so the sync state comes from the tree.
 * Belt-and-braces: the page already excludes synced items server-side
 * (EXCLUDED_SYNC_PROVIDERS), so this only still matters for linked rows and
 * not-yet-backfilled data.
 */
export const isSyncedContentItem = (item, categoriesById) =>
  isExternalSyncReadOnly(item?.options) ||
  !!item?.links?.some(link => isExternalSyncReadOnly(categoriesById?.[link.categoryId]?.options));

/**
 * The API executer returns the raw axios Error under `error`; React throws when one reaches a
 * toast body, so every failure is surfaced as a string.
 */
export const getResponseErrorMessage = response =>
  response?.data?.message || response?.error?.message || String(response?.error);

/**
 * Converts expiry status enum to date range filters for API
 */
export const convertExpiredStatusToDates = expiredStatus => {
  if (!expiredStatus) return {};

  const today = DateTime.now().startOf('day');

  if (expiredStatus === EXPIRY_STATUS.EXPIRED) {
    return { expiredBefore: today.toISODate() };
  } else if (expiredStatus === EXPIRY_STATUS.ABOUT_TO_EXPIRE) {
    const twoWeeksFromNow = today.plus({ weeks: 2 });
    return {
      expiredAfter: today.toISODate(),
      expiredBefore: twoWeeksFromNow.toISODate(),
    };
  } else if (expiredStatus === EXPIRY_STATUS.EXPIRES_IN_3_MONTHS) {
    const threeMonthsFromNow = today.plus({ months: 3 });
    return {
      expiredAfter: today.toISODate(),
      expiredBefore: threeMonthsFromNow.toISODate(),
    };
  }

  return {};
};

/**
 * Maps AG Grid column keys to API filter parameter names
 */
export const mapColumnKeysToApiParams = params => {
  const mapped = { ...params };

  // Map column names to API field names
  if (mapped.path) {
    mapped.categoryIds = mapped.path;
    delete mapped.path;
  }
  if (mapped.owners) {
    mapped.ownerIds = mapped.owners;
    delete mapped.owners;
  }
  if (mapped.specialty) {
    // Specialty filter sends to both specialtyIds and subspecialtyIds
    mapped.specialtyIds = mapped.specialty;
    mapped.subspecialtyIds = mapped.specialty;
    delete mapped.specialty;
  }
  if (mapped.expiresAt) {
    mapped.expiredStatus = mapped.expiresAt;
    delete mapped.expiresAt;
  }
  if (mapped.shared !== undefined) {
    mapped.sharedOnKnowledgeNetwork = mapped.shared;
    delete mapped.shared;
  }

  // Convert expiry status to date filters
  if (mapped.expiredStatus) {
    const dateFilters = convertExpiredStatusToDates(mapped.expiredStatus);
    Object.assign(mapped, dateFilters);
    delete mapped.expiredStatus;
  }

  // Map sort field names
  if (mapped.sortBy) {
    mapped.sortedBy = mapped.sortBy;
    delete mapped.sortBy;
  }
  if (mapped.sortOrder) {
    mapped.sortedOrder = mapped.sortOrder;
    delete mapped.sortOrder;
  }

  return mapped;
};
